import { useFocusEffect } from '@react-navigation/native';
import { useCallback, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';

import { getVipTicketWallet } from '../../services/api';
import { selectIsLogged, selectProfile } from '../../store/auth/authReducer';

/**
 * The player's VIP Ticket balance (`GET /api/vip-tickets`), held once for the whole app. There is
 * no store slice for it: a module cache shared by the Bonuses badge, the VIP Arcade gate and the
 * Inventory card, so a screen shows the last known balance at once and the wallet - a slow read,
 * gateway-game -> Kafka -> game-view -> NATS -> game - only refreshes it behind. The cache is
 * keyed by player so a different login never sees the previous player's balance.
 */
type CachedBalance = { playerId: string; balance: number };

let cached: CachedBalance | null = null;
// Bumped by every balance set from outside a read, so a read that started before it cannot
// write its older answer over it, and the next refresh starts a read of its own.
let localWrites = 0;
let inFlight: { playerId: string; promise: Promise<number | null>; writes: number } | null = null;
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const setCached = (next: CachedBalance) => {
	cached = next;
	listeners.forEach(listener => listener());
};

/** The last balance read for this player, or `null` if none has been read yet. */
export function peekVipTicketBalance(playerId: string): number | null {
	return cached?.playerId === playerId ? cached.balance : null;
}

/**
 * Reads the wallet again and updates every subscriber; calls made while a read is out share it.
 * Resolves `null` when the wallet cannot be read, leaving the last known balance in place.
 */
export function refreshVipTicketBalance(playerId: string): Promise<number | null> {
	if (inFlight?.playerId === playerId && inFlight.writes === localWrites) return inFlight.promise;
	const writes = localWrites;
	const promise = getVipTicketWallet()
		.then(wallet => {
			if (!Number.isFinite(wallet?.balance)) return null;
			if (writes === localWrites) setCached({ playerId, balance: wallet.balance });
			return wallet.balance;
		})
		.catch(err => {
			console.warn('[VipTickets] wallet unavailable', err);
			return null;
		})
		.finally(() => {
			if (inFlight?.promise === promise) inFlight = null;
		});
	inFlight = { playerId, promise, writes };
	return promise;
}

/**
 * A balance the backend just reported elsewhere - an arcade round's receipt, relayed by the game -
 * shown at once, ahead of the wallet read that confirms it.
 */
export function setVipTicketBalance(playerId: string, balance: number) {
	if (!Number.isFinite(balance)) return;
	localWrites += 1;
	setCached({ playerId, balance });
}

/** The logged-in player the balance belongs to, or `null` for a guest. */
export function useVipTicketPlayerId(): string | null {
	const isLogged = !!useSelector(selectIsLogged);
	const userId = useSelector(selectProfile)?.id;
	return isLogged ? String(userId ?? '') : null;
}

/**
 * The balance, refreshed each time the screen comes into focus so a ticket spent in the arcade or
 * granted as a reward shows on the way back. `null` until the first read, and when logged out -
 * callers hide the balance then rather than print a 0.
 */
export function useVipTicketBalance(): number | null {
	const playerId = useVipTicketPlayerId();
	const balance = useSyncExternalStore(subscribe, () =>
		playerId == null ? null : peekVipTicketBalance(playerId),
	);

	useFocusEffect(
		useCallback(() => {
			if (playerId != null) refreshVipTicketBalance(playerId);
		}, [playerId]),
	);

	return balance;
}
